/** SlaInternoTab — Editor de la matriz de SLA interno: Área Funcional x Prioridad Interna -> horas.
 *  Las celdas vacías significan "sin SLA". Guarda toda la matriz (full-replace). */
import React, { useState, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Loader2, Save, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../../../hooks/useAbortableLoad';

export default function SlaInternoTab({ api }) {
  const { user } = useAuth();
  const isAdmin = ['ADMINISTRADOR', 'SUPERADMIN'].includes(user?.rol);
  const [areas, setAreas] = useState([]);
  const [prioridades, setPrioridades] = useState([]);
  const [matrix, setMatrix] = useState({}); // key `${areaId}|${priId}` -> horas (string)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const [ar, pr, sla] = await Promise.all([
        api.get('/areas-funcionales?incluir_inactivas=false', { signal }),
        api.get('/tickets/prioridades-internas', { signal }),
        api.get('/tickets/sla-interno', { signal }),
      ]);
      setAreas(ar.data || []);
      setPrioridades((pr.data || []).filter((p) => p.activo));
      const m = {};
      (sla.data || []).forEach((c) => { m[`${c.area_funcional_id}|${c.prioridad_interna_id}`] = String(c.horas_sla); });
      setMatrix(m);
    } catch (e) { if (isCanceled(e)) return; toast.error('Error cargando la matriz de SLA interno'); }
    finally { setLoading(false); }
  }, [api]);

  useAbortableLoad((signal) => load(signal), [load]);

  const setCell = (areaId, priId, val) => {
    setMatrix((prev) => ({ ...prev, [`${areaId}|${priId}`]: val.replace(/[^0-9]/g, '') }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const celdas = [];
      Object.entries(matrix).forEach(([k, v]) => {
        const horas = parseInt(v, 10);
        if (horas > 0) {
          const [area_funcional_id, prioridad_interna_id] = k.split('|');
          celdas.push({ area_funcional_id, prioridad_interna_id, horas_sla: horas });
        }
      });
      await api.put('/tickets/sla-interno', { celdas });
      toast.success('Matriz de SLA interno guardada');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error guardando la matriz'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  if (areas.length === 0 || prioridades.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-8 text-center" data-testid="sla-interno-empty">
        Necesitas al menos un <strong>Área Funcional</strong> y una <strong>Prioridad Interna</strong> activas para configurar la matriz.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="sla-interno-tab">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600 flex items-center gap-1.5">
          <Clock className="w-4 h-4" /> Horas de SLA por <strong>Área</strong> y <strong>Prioridad interna</strong>. Vacío = sin SLA.
        </p>
        {isAdmin && (
          <Button size="sm" onClick={save} disabled={saving} data-testid="guardar-sla-interno-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Guardar matriz
          </Button>
        )}
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left p-3 font-medium text-slate-600 sticky left-0 bg-slate-50">Área Funcional</th>
              {prioridades.map((p) => (
                <th key={p.id} className="p-3 font-medium text-slate-600 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color || '#94a3b8' }} />
                    {p.nombre}{p.es_default && <span className="text-[10px] text-emerald-600">(def)</span>}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {areas.map((a) => (
              <tr key={a.id} className="border-b border-slate-100" data-testid={`sla-row-${a.id}`}>
                <td className="p-3 font-medium text-slate-800 sticky left-0 bg-white whitespace-nowrap">{a.nombre}</td>
                {prioridades.map((p) => (
                  <td key={p.id} className="p-2 text-center">
                    <Input
                      type="number" min={1}
                      className="w-20 mx-auto text-center"
                      placeholder="—"
                      value={matrix[`${a.id}|${p.id}`] || ''}
                      onChange={(e) => setCell(a.id, p.id, e.target.value)}
                      disabled={!isAdmin}
                      data-testid={`sla-cell-${a.id}-${p.id}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">Las horas se interpretan como tiempo máximo de atención del responsable interno desde la derivación.</p>
    </div>
  );
}
